import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireAuth, RequireFeature, RequireSuperAdmin } from '../auth/RequireAuth';
import { AppLayout } from './AppLayout';
import { LoginPage } from '../pages/auth/LoginPage';
import { RegisterPage } from '../pages/auth/RegisterPage';
import { AcceptInvitePage } from '../pages/auth/AcceptInvitePage';
import { ForgotPasswordPage } from '../pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '../pages/auth/ResetPasswordPage';
import { DashboardPage } from '../pages/DashboardPage';
import { ImportsPage } from '../pages/imports/ImportsPage';
import { ImportWizardPage } from '../pages/imports/ImportWizardPage';
import { ImportJobPage } from '../pages/imports/ImportJobPage';
import { UsersPage } from '../pages/admin/UsersPage';
import { SettingsPage } from '../pages/admin/SettingsPage';
import { AccountPage } from '../pages/AccountPage';
import { FieldsPage } from '../pages/admin/FieldsPage';
import { ProjectsPage } from '../pages/admin/ProjectsPage';
import { ContactsPage } from '../pages/contacts/ContactsPage';
import { ContactProfilePage } from '../pages/contacts/ContactProfilePage';
import { OrganizationsPage } from '../pages/organizations/OrganizationsPage';
import { OrganizationProfilePage } from '../pages/organizations/OrganizationProfilePage';
import { TagsPage } from '../pages/audience/TagsPage';
import { ListsPage } from '../pages/audience/ListsPage';
import { ListDetailPage } from '../pages/audience/ListDetailPage';
import { SegmentsPage } from '../pages/audience/SegmentsPage';
import { SegmentEditorPage } from '../pages/audience/SegmentEditorPage';
import { DomainsPage } from '../pages/admin/DomainsPage';
import { AgentsPage } from '../pages/admin/AgentsPage';
import { InboxPage } from '../pages/inbox/InboxPage';
import { PipelinesPage } from '../pages/admin/PipelinesPage';
import { ScanCardsPage } from '../pages/scan/ScanCardsPage';
import { ScanReviewPage } from '../pages/scan/ScanReviewPage';
import { PipelinePage } from '../pages/deals/PipelinePage';
import { DealPage } from '../pages/deals/DealPage';
import { FormsPage } from '../pages/forms/FormsPage';
import { FormEditorPage } from '../pages/forms/FormEditorPage';
import { PublicFormPage } from '../pages/forms/PublicFormPage';
import { TemplatesPage } from '../pages/campaigns/TemplatesPage';
import { SequencesPage } from '../pages/coldEmail/SequencesPage';
import { SequencePage } from '../pages/coldEmail/SequencePage';
import { CampaignsPage } from '../pages/campaigns/CampaignsPage';
import { CampaignEditorPage } from '../pages/campaigns/CampaignEditorPage';
import { VerificationPage } from '../pages/admin/VerificationPage';
import { DuplicatesPage } from '../pages/audience/DuplicatesPage';
import { NameCleanupPage } from '../pages/tools/NameCleanupPage';
import { OddContactsPage } from '../pages/tools/OddContactsPage';
import { NoContactInfoPage } from '../pages/tools/NoContactInfoPage';
import { ChannelCleanerPage } from '../pages/tools/ChannelCleanerPage';
import { DuplicatePhonesPage } from '../pages/tools/DuplicatePhonesPage';
import { AddressMergerPage } from '../pages/tools/AddressMergerPage';
import { AsanaImportPage } from '../pages/tools/AsanaImportPage';
import { NotesImportPage } from '../pages/tools/NotesImportPage';
import { WarmupPage } from '../pages/tools/WarmupPage';
import { DomainHealthPage } from '../pages/tools/DomainHealthPage';
import { WarmupSettingsPage } from '../pages/admin/WarmupSettingsPage';
import { NotFoundPage } from '../pages/NotFoundPage';
import { FeaturesPage } from '../pages/admin/FeaturesPage';
import { NotificationsPage } from '../pages/admin/NotificationsPage';
import { MyTasksPage, TasksPage } from '../pages/tasks/TasksPage';
import { TaskPage } from '../pages/tasks/TaskPage';
import { TaskActivityPage } from '../pages/tasks/TaskActivityPage';
import { MovedTo, SectionIndex, SectionLayout } from '../components/SectionLayout';
import { ToolsLayout } from './ToolsLayout';
import { SETTINGS_SECTIONS, TOOLS_SECTIONS } from './sections';

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/register', element: <RegisterPage /> },
  { path: '/accept-invite', element: <AcceptInvitePage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // public: the API answers 404 for every form while Forms is switched off
  { path: '/f/:slug', element: <PublicFormPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: '/', element: <DashboardPage /> },
          { path: '/contacts', element: <ContactsPage /> },
          { path: '/contacts/:id', element: <ContactProfilePage /> },
          { path: '/organizations', element: <OrganizationsPage /> },
          { path: '/organizations/:id', element: <OrganizationProfilePage /> },
          { path: '/segments', element: <SegmentsPage /> },
          { path: '/segments/:id', element: <SegmentEditorPage /> },
          { path: '/lists', element: <ListsPage /> },
          { path: '/lists/:id', element: <ListDetailPage /> },
          { path: '/tags', element: <TagsPage /> },
          { element: <RequireFeature feature="inbox" />, children: [{ path: '/inbox', element: <InboxPage /> }] },
          {
            element: <RequireFeature feature="tasks" />,
            children: [
              { path: '/tasks', element: <TasksPage /> },
              { path: '/tasks/my', element: <MyTasksPage /> },
              { path: '/tasks/activity', element: <TaskActivityPage /> },
              { path: '/tasks/:id', element: <TaskPage /> },
            ],
          },
          {
            element: <RequireFeature feature="pipelines" />,
            children: [
              { path: '/pipeline', element: <PipelinePage /> },
              { path: '/deals/:id', element: <DealPage /> },
            ],
          },
          {
            element: <RequireFeature feature="forms" />,
            children: [
              { path: '/forms', element: <FormsPage /> },
              { path: '/forms/:id', element: <FormEditorPage /> },
            ],
          },
          {
            element: <RequireFeature feature="campaigns" />,
            children: [
              { path: '/campaigns', element: <CampaignsPage /> },
              { path: '/campaigns/:id', element: <CampaignEditorPage /> },
              { path: '/templates', element: <TemplatesPage /> },
              { path: '/cold-email', element: <SequencesPage /> },
              { path: '/cold-email/:id', element: <SequencePage /> },
            ],
          },
          { path: '/account', element: <AccountPage /> },
          {
            path: '/settings',
            element: <SectionLayout title="Settings" items={SETTINGS_SECTIONS} />,
            children: [
              { index: true, element: <SectionIndex items={SETTINGS_SECTIONS} /> },
              { path: 'projects', element: <ProjectsPage /> },
              { path: 'fields', element: <FieldsPage /> },
              { element: <RequireFeature feature="pipelines" />, children: [{ path: 'pipelines', element: <PipelinesPage /> }] },
              { path: 'agents', element: <AgentsPage /> },
              { path: 'domains', element: <DomainsPage /> },
              { path: 'warmup', element: <WarmupSettingsPage /> },
              {
                element: <RequireSuperAdmin />,
                children: [
                  { path: 'general', element: <SettingsPage /> },
                  { path: 'users', element: <UsersPage /> },
                  { path: 'features', element: <FeaturesPage /> },
                  { path: 'notifications', element: <NotificationsPage /> },
                ],
              },
            ],
          },
          {
            path: '/tools',
            element: <ToolsLayout />,
            children: [
              { index: true, element: <SectionIndex items={TOOLS_SECTIONS} /> },
              {
                element: <RequireFeature feature="cardScanner" />,
                children: [
                  { path: 'scan-cards', element: <ScanCardsPage /> },
                  { path: 'scan-cards/:id', element: <ScanReviewPage /> },
                ],
              },
              { path: 'verification', element: <VerificationPage /> },
              { path: 'warmup', element: <WarmupPage /> },
              { path: 'domain-health', element: <DomainHealthPage /> },
              { path: 'imports', element: <ImportsPage /> },
              { path: 'imports/new', element: <ImportWizardPage /> },
              { path: 'imports/:id', element: <ImportJobPage /> },
              { path: 'notes-import', element: <NotesImportPage /> },
              { element: <RequireFeature feature="tasks" />, children: [{ path: 'asana-import', element: <AsanaImportPage /> }] },
              { path: 'duplicates', element: <DuplicatesPage /> },
              { path: 'name-cleanup', element: <NameCleanupPage /> },
              { path: 'odd-contacts', element: <OddContactsPage /> },
              { path: 'email-phone-cleaner', element: <ChannelCleanerPage /> },
              { path: 'duplicate-phones', element: <DuplicatePhonesPage /> },
              { path: 'address-merger', element: <AddressMergerPage /> },
              { path: 'no-contact-info', element: <NoContactInfoPage /> },
            ],
          },
          // old addresses, kept for bookmarks
          { path: '/admin', element: <Navigate to="/settings" replace /> },
          { path: '/admin/settings', element: <Navigate to="/settings/general" replace /> },
          { path: '/admin/verification', element: <Navigate to="/tools/verification" replace /> },
          { path: '/admin/*', element: <MovedTo from="/admin" to="/settings" /> },
          { path: '/duplicates', element: <Navigate to="/tools/duplicates" replace /> },
          { path: '/imports/*', element: <MovedTo from="/imports" to="/tools/imports" /> },
          { path: '/scan/*', element: <MovedTo from="/scan" to="/tools/scan-cards" /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
